import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Button, Checkbox, Form, Row, Select, Space, Tooltip, Typography } from 'antd';
import {
    DropboxOutlined,
    InfoCircleOutlined,
    MinusCircleOutlined,
    MinusOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { applyLang } from '../layout';
import { computeReducedPrice } from './index';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import dayjs from 'dayjs';
import { formatDayjs } from "../../../services/localeFormat";
import LocalizedFormat from "dayjs/plugin/localizedFormat"
import logger from "../../../services/logger"
dayjs.extend(LocalizedFormat)

const { Text, Title } = Typography;

function DiscountNotice({percent, discountCents, priceCents, reducedAt, formPlugin, lang}) {
    const date = formatDayjs(reducedAt, lang, "LLLL")
    const initial = formPlugin.currencyFormat(priceCents / 100)
    const amount = formPlugin.currencyFormat(discountCents / 100)
    return <span style={{color: "red"}}>{percent !== null
        ? t`Réduction de ${percent} % jusqu'au ${date} (prix initial : ${initial})`
        : t`Réduction de ${amount} jusqu'au ${date} (prix initial : ${initial})`}</span>
}


var asset_url = "https://kehilaapi.syn-assist.fr/medias/"
if (process.env.NODE_ENV === "production") {
    asset_url = "https://api.kehila.syn-assist.fr/medias/"
}

function CustomTooltip(props) {
    if (!props.title) {
        return props.children;
    }

    var tooltipProps = { ...props };
    tooltipProps.trigger = props.trigger || "click";

    return (
        <div style={{ display: "flex" }}>
            {props.children}
            <div style={{ marginLeft: 5 }}>
                <Tooltip {...tooltipProps}>
                    <InfoCircleOutlined style={{ color: "rgb(114, 132, 154)", fontSize: 14 }} />
                </Tooltip>
            </div>
        </div>
    );
}

const ProductField = ({ value, onChange, product, formPlugin, liveRender, device }) => {
    const withPropagation = useRef(true);
    const [stock, setStock] = useState(Infinity);
    const [_updateValue, forceUpdate] = useReducer(x => x + 1, 0);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    // Race-safe : ignore la réponse si un nouvel appel est en cours ou si démontage
    useEffect(() => {
        let cancelled = false;
        const propagate = withPropagation.current;
        withPropagation.current = true;
        formPlugin.getAvailableProduct(product.id, value, propagate)
            .then(total => {
                if (cancelled) return;
                setStock(prev => (prev !== total ? total : prev));
            })
            .catch(err => {
                if (cancelled) return;
                logger.error({ err }, "[product] getAvailableProduct failed");
                // En cas d'erreur réseau, on bloque les commandes par sécurité (pas de stock infini par défaut)
                setStock(0);
            });
        return () => { cancelled = true; };
    }, [value, calendarsValues, _updateValue, product.id]);

    useEffect(() => {
        const callback = () => {
            withPropagation.current = false;
            forceUpdate();
        };
        formPlugin.addProductStockRef(product.id, callback);
        // Note: pas de unregister exposé par formPlugin actuellement.
        // L'ancien addProductStockRef remplace par id donc fuite limitée.
    }, [product.id]);

    const finalValue = value || 0;
    const isOver = (stock <= 0 && finalValue === 0);
    const isAvailable = stock - 1 >= 0;

    // Garde-fou : si le serveur indique stock négatif (sur-commande détectée),
    // on clampe la quantité à 0 ou au stock disponible. Déplacé en useEffect
    // pour éviter un setState pendant le render.
    useEffect(() => {
        if (stock < 0) {
            onChange(stock <= 0 ? 0 : Math.max(0, finalValue + stock));
        }
    }, [stock, finalValue]);

    const setValue = (v) => {
        if (v < 0 || (v > finalValue && !isAvailable)) return false;
        onChange(v);
    };

    if (isOver && product?.hideOverStock) return null;

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"



    const { reduced, priceCents, reducedPriceCents } = computeReducedPrice(product, { withPaiement: formPlugin.formData.withPaiement, devise: formPlugin.org?.devise, baseDevise: formPlugin.formData?.devise })

    const renderOptions = product?.renderOptions || {};


    return (
        <div style={{
            display: "flex",
            flexDirection: "row",
            gap:20,
            padding: "10px 5%",
            paddingTop: 20,
            backgroundColor: isOver ? "#EDEDED" : "transparent",
            borderRadius: 10,
            border: "1px solid #1D1D1B33",
            userSelect: "none",
        }}>

            {!!renderOptions?.image &&(
                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <img src={asset_url+renderOptions.image} style={{height:device === "MOBILE"?40:50,width:device === "MOBILE"?40:50,borderRadius:"100%",objectFit:"center"}}/>
                </div>
            )}

            <div style={{display:'flex',flex:1,flexDirection:"column",justifyContent: "space-between",}}>
                <div>
                    <Title className={"layout-label"} level={device === "MOBILE" ? 5 : 4}>
                        <CustomTooltip title={product.description}>{product.title}</CustomTooltip>
                    </Title>
                </div>

                <Row style={{ justifyContent: "space-between", alignItems: "center", padding: device === "MOBILE" ? 0 : "0 20px" }}>
                    <Space>
                        {formPlugin.formData.withPaiement && priceCents > 0 && (
                            <>
                                {reduced?(
                                    <div style={{display:"flex",gap:10}}>
                                        <Text className={"layout-label"} style={{textDecorationLine:"line-through"}}>{formPlugin.currencyFormat(priceCents / 100)} {isAbo && (<Trans>/ Mois</Trans>)}</Text>
                                        <Text className={"layout-label"} style={{fontWeight:"bold"}}>{formPlugin.currencyFormat(reducedPriceCents/100)} {isAbo && (<Trans>/ Mois</Trans>)}</Text>
                                    </div>
                                ):(
                                    <Text className={"layout-label"}>{formPlugin.currencyFormat(priceCents / 100)} {isAbo && (<Trans>/ Mois</Trans>)}</Text>
                                )}
                            </>
                        )}
                        {stock < Infinity && !isOver && !renderOptions?.hideAvailableStock && (
                            <Text className={"layout-label"} type="secondary" style={{ fontSize: device === "MOBILE" ? 12 : 15 }}>
                                {stock} <Trans>disponible</Trans>
                            </Text>
                        )}
                        {isOver && (
                            <Text className={"layout-label"} type="secondary" style={{ color: "red", fontSize: device === "MOBILE" ? 12 : 15 }}>
                                <Trans>Épuisé</Trans>
                            </Text>
                        )}
                    </Space>

                    <div style={{
                        width: 95,
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: 5,
                    }}>
                        <div
                            style={{
                                backgroundColor: ((liveRender || isOver) && finalValue > 0) ? "#000" : "rgba(0,0,0,0.2)",
                                color: "#FFF",
                                borderRadius: "100%",
                                width: 28,
                                height: 28,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: ((liveRender || isOver) && finalValue > 0) ? "pointer" : "auto"
                            }}
                            onClick={(liveRender || isOver) ? () => setValue(finalValue - 1) : null}
                        >
                            <MinusOutlined />
                        </div>

                        <span style={{ fontSize: 16 }}>{finalValue}</span>

                        <div
                            style={{
                                backgroundColor: (liveRender || isOver || !isAvailable) ? "#000" : "rgba(0,0,0,0.2)",
                                color: "#FFF",
                                borderRadius: "100%",
                                width: 28,
                                height: 28,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer"
                            }}
                            onClick={(liveRender || isOver || !isAvailable) ? () => setValue(finalValue + 1) : null}
                        >
                            <PlusOutlined />
                        </div>
                    </div>
                </Row>
            </div>
        </div>
    );
};

const ProductSelect = ({ value, onChange, product, formPlugin, liveRender }) => {
    const withPropagation = useRef(true);
    const [stock, setStock] = useState(Infinity);
    const [_updateValue, forceUpdate] = useReducer(x => x + 1, 0);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    useEffect(() => {
        let cancelled = false;
        const propagate = withPropagation.current;
        withPropagation.current = true;
        formPlugin.getAvailableProduct(product.id, value, propagate)
            .then(total => {
                if (cancelled) return;
                setStock(prev => (prev !== total ? total : prev));
            })
            .catch(err => {
                if (cancelled) return;
                logger.error({ err }, "[product] getAvailableProduct failed");
                setStock(0);
            });
        return () => { cancelled = true; };
    }, [value, calendarsValues, _updateValue, product.id]);

    useEffect(() => {
        formPlugin.addProductStockRef(product.id, () => {
            withPropagation.current = false;
            forceUpdate();
        });
    }, [product.id]);

    const finalValue = value || 0;
    const isOver = (stock <= 0 && finalValue === 0);
    const isAvailable = stock - 1 >= 0;

    useEffect(() => {
        if (stock < 0 && finalValue > 0) onChange(0);
    }, [stock, finalValue, onChange]);

    let maxItems = 30;
    if (stock !== Infinity && (stock + finalValue) > 0 && (stock + finalValue) < maxItems) {
        maxItems = (stock + finalValue);
    }

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"

    const { reduced, priceCents, reducedPriceCents } = computeReducedPrice(product, { withPaiement: formPlugin.formData.withPaiement, devise: formPlugin.org?.devise, baseDevise: formPlugin.formData?.devise })
    const reducedPriceUnit = reduced ? reducedPriceCents / 100 : null

    const renderOptions = product?.renderOptions || {};
    const options = Array.from(Array(maxItems).keys()).map(index => {
        let _value = index + 1;
        let _label = "";

        if (renderOptions.selectPrefix) _label += renderOptions.selectPrefix + " ";
        _label += _value + " ";

        _label += _value === 1
            ? (renderOptions.selectProductName || product.title)
            : (renderOptions.selectProductNameMulti || product.title);


        if (priceCents > 0) {
            const unitPrice = reducedPriceUnit !== null ? reducedPriceUnit : priceCents / 100
            if(isAbo){
                _label += t` (${formPlugin.currencyFormat(unitPrice * _value)} / Mois)`;
            }else{
                _label += " (" + formPlugin.currencyFormat(unitPrice * _value) + ")";
            }
        }

        return { value: _value, label: _label };
    });

    if (isOver && product?.hideOverStock) return null;


    return (
        <select onChange={(e) => onChange(parseInt(e.target.value))} value={finalValue} className={"form-control"}
                disabled={(stock !== Infinity && isOver && liveRender)}>
            <option value={0}>{!isOver && t`Choisir une option`} {isOver && t`Épuisé`}</option>
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    );
};


const ProductCheckbox = ({ product, formPlugin, lang }) => {
    const form = Form.useFormInstance();
    const value = Form.useWatch(product.id + '_quantity', form) || 0;
    const withPropagation = useRef(true);
    const [stock, setStock] = useState(Infinity);
    const [stockRevision, refreshStock] = useReducer(x => x + 1, 0);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    useEffect(() => {
        let cancelled = false;
        const propagate = withPropagation.current;
        withPropagation.current = true;
        formPlugin.getAvailableProduct(product.id, value, propagate)
            .then(total => {
                if (cancelled) return;
                setStock(prev => (prev !== total ? total : prev));
            })
            .catch(err => {
                if (cancelled) return;
                logger.error({ err }, "[product] getAvailableProduct failed");
                setStock(0);
            });
        return () => { cancelled = true; };
    }, [value, calendarsValues, product.id, stockRevision]);

    useEffect(() => {
        formPlugin.addProductStockRef(product.id, () => {
            withPropagation.current = false;
            refreshStock();
        });
    }, [product.id, formPlugin]);

    useEffect(() => {
        if (stock < 0 && value > 0) {
            form.setFieldValue(product.id + '_quantity', 0);
            formPlugin.syncFromForm(form);
        }
    }, [stock, value, form, formPlugin, product.id]);

    const isOver = stock < 0 || (stock <= 0 && value === 0);
    const renderOptions = product?.renderOptions || {};

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"

    const { reduced, priceCents, percent: reducedPercent, discountCents, reducedPriceCents, expiresAt } = computeReducedPrice(product, { withPaiement: formPlugin.formData.withPaiement, devise: formPlugin.org?.devise, baseDevise: formPlugin.formData?.devise })
    const reducedPriceUnit = reduced ? reducedPriceCents / 100 : null
    const reducedAt = expiresAt || new Date()

    let title = product.title;
    if (priceCents > 0) {
        const displayPrice = reducedPriceUnit !== null ? reducedPriceUnit : priceCents / 100
        title += " - " + formPlugin.currencyFormat(displayPrice);
        if(isAbo){
            title += t` / Mois`;
        }
    }

    let checkBoxLabel = renderOptions?.checkboxLabel || title;

    if(renderOptions?.checkboxHideLabel){
        title=""
    }

    if (isOver && product?.hideOverStock) return null;

    const CheckBoxComponent = (_props)=>
    {
        const [checked, setChecked] = useState(_props.value===1);
        let _value=checked?1:0

        useEffect(() => {
            if(_value!==_props.value){
                _props.onChange(_value);
            }
        },[checked])

        useEffect(() => {
            if(_value!==_props.value){
               setChecked(_props.value?true:false)
            }
        },[_props.value]);

        return(
            <Checkbox checked={checked} onChange={(e)=>setChecked(e.target.checked)} disabled={isOver}>
                <div style={{userSelect:"none"}} className={"layout-label"}>{checkBoxLabel}</div>
            </Checkbox>
        )
    }

    return (
        <div style={{marginBottom:reduced?50:0}}>
            <Form.Item
                name={product.id + '_quantity'}
                label={(
                    <Space>
                        {title}
                        {isOver && (
                            <Text className={"layout-label"} style={{ color: "red" }}>
                                <Trans>Épuisé</Trans>
                            </Text>
                        )}
                    </Space>
                )}

                help={reduced && (
                    <DiscountNotice percent={reducedPercent} discountCents={discountCents} priceCents={priceCents} reducedAt={reducedAt} formPlugin={formPlugin} lang={lang}/>
                )}
            >
                <CheckBoxComponent/>
            </Form.Item>
        </div>
    );
};

export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {
            if (props.remove) formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id);
            const { data } = props;
            const liveRender = (props.isPreviewMode || formPlugin.live);
            const productList = data.productList || [];

            if (formPlugin.formData.type !== "COMPLEX") {
                if (props.remove) props.remove();
                return null;
            }

            const renderProduct = (product) => {
                product = applyLang(product, { value: props.lang });
                const type = product?.renderOptions?.type || "DEFAULT";

                const { reduced, priceCents, percent: reducedPercent, discountCents, expiresAt } = computeReducedPrice(product, { withPaiement: formPlugin.formData.withPaiement, devise: formPlugin.org?.devise, baseDevise: formPlugin.formData?.devise })
                const reducedAt = expiresAt || new Date()

                if (type === "SELECT") {
                    return (
                        <div style={{marginBottom:reduced?50:0}}>
                            <Form.Item
                                label={product.title}
                                tooltip={product.description ? { title: product.description, icon: <InfoCircleOutlined /> } : null}
                                name={product.id + '_quantity'}
                                help={reduced && (
                                    <DiscountNotice percent={reducedPercent} discountCents={discountCents} priceCents={priceCents} reducedAt={reducedAt} formPlugin={formPlugin} lang={props.lang}/>
                                )}
                            >
                                <ProductSelect product={product} formPlugin={formPlugin} liveRender={liveRender} />
                            </Form.Item>
                        </div>
                    );
                }

                if (type === "CHECKBOX") {
                    return <ProductCheckbox product={product} formPlugin={formPlugin} lang={props.lang} />;
                }

                return (
                    <div style={{marginBottom:reduced?50:0}}>
                        <Form.Item
                            name={product.id + '_quantity'}
                            help={reduced && (
                                <DiscountNotice percent={reducedPercent} discountCents={discountCents} priceCents={priceCents} reducedAt={reducedAt} formPlugin={formPlugin} lang={props.lang}/>
                            )}
                        >
                            <ProductField product={product} formPlugin={formPlugin} liveRender={liveRender} device={props.device} />
                        </Form.Item>
                    </div>

                );
            };

            return (
                <div style={{
                    minHeight: liveRender ? 0 : 50,
                    border: liveRender ? 0 : "1px dashed red",
                    borderRadius: liveRender ? 0 : "10px"
                }}>
                    {productList.map(p => {
                        if (!p) return null;
                        let product = formPlugin.formData.products.find(i => i.id === p.productId);
                        if (product && product.active) return renderProduct(product);
                    })}
                </div>
            );
        },
        namespace,
        id: 'form-product' + namespace,
        title: t`Produit`,
        description: t`Élément de formulaire utilisable.`,
        icon: <DropboxOutlined />,
        iconColor: "#8E44AD",
        version: 1,
        hideInMenu: formPlugin.formData.type !== "COMPLEX",
        allowClickInside: false,
        controls: {
            type: 'custom',
            Component: (props) => {
                const products = formPlugin.formData.products;
                const productList = props.data?.productList || [];
                const defaultValue = { productList: productList };

                return (
                    <Form initialValues={defaultValue} onValuesChange={(_, values) => props.onChange(values)}>
                        <Form.List name={"productList"} label={t`Produits`}>
                            {(fields, { add, remove }) => (
                                <div>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item name={[name, "productId"]} label={t`Produit`} required>
                                                <Select placeholder={t`Choisir un produit`}>
                                                    {products.map(i => (
                                                        <Select.Option value={i.id}>{i.label}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            <Trans>Ajouter un produit</Trans>
                                        </Button>
                                    </Form.Item>
                                </div>
                            )}
                        </Form.List>
                    </Form>
                );
            }
        },
    };
};
